import _isEmpty from 'lodash/isEmpty';

/**
 * Utility to determine filter hierarchy and filter data
 * Priority order:
 * 1) Strong Attribute filter
 * 2) Truck/SUV Bodystyle substyle (Truck cab size filter, SUV 3rd row filter)
 * 3) Vehicle Features
 * 4) Suggested filter
 */

// Constants
const VEHICLE_FEATURE_CODES = [
    '1211', // Bluetooth, Hands-Free
    '1224', // Backup Camera
    '1013', // Security System
    '1313', // Adaptive Cruise Control
    '1136', // Premium Audio
    '1330', // Cross Traffic Alert
    '1310', // Lane Departure Warning
    '1229', // Keyless Start
    '1221', // Remote Engine Start
    '1331', // WiFi Hotspot
];

// Export constants
export { VEHICLE_FEATURE_CODES };

/**
 * Generate unique filter key based on filter type and data
 */
const generateFilterKey = (type, data) => {
    switch (type) {
        case 'strongAttribute':
            return `strong-${JSON.stringify(data?.codes)}`;
        case 'truck':
            return 'truck-cab-size';
        case 'suv':
            return 'suv-third-row';
        case 'vehicleFeature':
            return `feature-${data?.value}`;
        case 'suggested':
            return `suggested-${JSON.stringify(data?.codes)}`;
        default:
            return `unknown-${type}`;
    }
};

/**
 * Generate filter key from candidate filter object
 */
const generateFilterKeyFromCandidate = (filter) => generateFilterKey(filter.type, filter.data);

/**
 * Check if a filter is already active/selected
 * Handles both regular filters (with codes) and vehicle features (with value/filterName)
 */
export const isFilterAlreadyActive = (filter, activeFilterNames, activeFeatureValues = []) => {
    // Handle regular filters with codes property (suggested filters, strong attribute filters)
    if (filter?.codes) {
        // Check if any of the filter code types are already active
        // For example, if filter has codes: { makeCode: ['HONDA'] }
        // and activeFilterNames contains 'makeCode', then it's active
        return Object.keys(filter.codes).some((filterKey) => activeFilterNames.includes(filterKey));
    }

    // Handle vehicle features with value property
    if (filter?.value) {
        // Check if the specific feature value is already selected
        if (activeFeatureValues.includes(filter.value)) {
            return true;
        }

        // Also check if the feature's filterName is in activeFilterNames
        if (filter.filterName && activeFilterNames.includes(filter.filterName)) {
            return true;
        }
    }

    return false;
};

/**
 * Get available vehicle features with counts (excluding already selected features)
 */
export const getAvailableVehicleFeatures = (featureFilterOptions) => {
    if (!featureFilterOptions?.options) return [];

    const availableFeatures = featureFilterOptions.options.filter(
        (option) => VEHICLE_FEATURE_CODES.includes(option.value)
            && option.count > 0
            && !option.selected
    );

    return availableFeatures;
};

/**
 * Get the first unseen vehicle feature that is not already active
 */
export const getFirstUnseenVehicleFeature = (availableFeatures, activeFeatureValues = [], activeFilterNames = []) => {
    if (!availableFeatures?.length) return null;

    const result = availableFeatures.find((feature) => {
        // Use the enhanced isFilterAlreadyActive function for consistent checking
        if (isFilterAlreadyActive(feature, activeFilterNames, activeFeatureValues)) {
            return false;
        }

        return true;
    });

    return result || null;
};

/**
 * Get up to 10 filters following the hierarchy priority
 * Returns an array of filter objects without marking them as seen
 */
export const getFilterSet = (params = {}) => {
    const {
        activeFilterValues = [],
        suggestedFilters = [],
        strongAttributeFilters = [],
        isTruckSelected = false,
        isSUVSelected = false,
        featureFilterOptions = null,
        featureFilterValues = [],
        cabSizeOptions = [],
        cabSizeValues = [],
    } = params;

    const filters = [];
    const activeFilterNames = (activeFilterValues || []).map((filter) => filter.filterName);
    const usedFilterKeys = new Set();

    // Helper to check if filter is already used or active
    const isFilterUsable = (candidateFilter, activeNames, activeFeatures = []) => {
        const actualFilter = candidateFilter.data || candidateFilter;
        if (isFilterAlreadyActive(actualFilter, activeNames, activeFeatures)) return false;

        // Generate unique key to check if we've already added this filter to the set
        const filterKey = generateFilterKeyFromCandidate(candidateFilter);

        // Check if already used in this set
        return !usedFilterKeys.has(filterKey);
    };

    // Helper to add filter to set
    const addFilter = (type, data) => {
        const filterKey = generateFilterKey(type, data);
        usedFilterKeys.add(filterKey);
        filters.push({ type, data });
    };

    // Priority 1: Strong Attribute filters
    if (strongAttributeFilters?.length > 0) {
        for (const filter of strongAttributeFilters) {
            if (filters.length >= 10) break;
            const candidateFilter = { type: 'strongAttribute', data: filter };
            if (isFilterUsable(candidateFilter, activeFilterNames)) {
                addFilter('strongAttribute', filter);
            }
        }
    }

    // Priority 2a: Truck filter
    if (filters.length < 10) {
        const thirdRowFilterOptions = featureFilterOptions?.options?.find(
            (option) => option.value === '1306'
        );
        const cabSizeApplied = cabSizeValues.length > 0;

        const shouldShowTruckCard = isTruckSelected && !cabSizeApplied && !_isEmpty(cabSizeOptions);

        if (shouldShowTruckCard) {
            const candidateFilter = { type: 'truck', data: {} };
            if (isFilterUsable(candidateFilter, activeFilterNames)) {
                addFilter('truck', {});
            }
        }

        // Priority 2b: SUV filter
        if (filters.length < 10) {
            const thirdRowApplied = featureFilterValues.includes('1306');
            const shouldShowSUVCard = isSUVSelected && !thirdRowApplied && !_isEmpty(thirdRowFilterOptions);

            if (shouldShowSUVCard) {
                const candidateFilter = { type: 'suv', data: {} };
                if (isFilterUsable(candidateFilter, activeFilterNames)) {
                    addFilter('suv', {});
                }
            }
        }
    }

    // Priority 3: Vehicle Features
    if (filters.length < 10) {
        const availableVehicleFeatures = getAvailableVehicleFeatures(featureFilterOptions);

        for (const feature of availableVehicleFeatures) {
            if (filters.length >= 10) break;
            const candidateFilter = { type: 'vehicleFeature', data: feature };
            if (isFilterUsable(candidateFilter, activeFilterNames, featureFilterValues)) {
                addFilter('vehicleFeature', feature);
            }
        }
    }

    // Priority 4: Suggested filters
    if (filters.length < 10 && suggestedFilters?.length > 0) {
        for (const filter of suggestedFilters) {
            if (filters.length >= 10) break;
            const candidateFilter = { type: 'suggested', data: filter };
            if (isFilterUsable(candidateFilter, activeFilterNames)) {
                addFilter('suggested', filter);
            }
        }
    }

    return filters;
};

/**
 * Get a single filter at a specific index from the complete filter hierarchy
 * Used to get the next filter when replacing a dismissed card
 */
export const getFilterAtIndex = (targetIndex, params = {}, excludeFilterKeys = new Set()) => {
    const {
        activeFilterValues = [],
        suggestedFilters = [],
        strongAttributeFilters = [],
        isTruckSelected = false,
        isSUVSelected = false,
        featureFilterOptions = null,
        featureFilterValues = [],
        cabSizeOptions = [],
        cabSizeValues = [],
    } = params;

    const activeFilterNames = (activeFilterValues || []).map((filter) => filter.filterName);
    let currentIndex = 0;

    // Helper to check if filter is already used or active
    const isFilterUsable = (candidateFilter, activeNames, activeFeatures = []) => {
        const actualFilter = candidateFilter.data || candidateFilter;
        if (isFilterAlreadyActive(actualFilter, activeNames, activeFeatures)) return false;

        // Generate unique key to check if we've already added this filter to the set
        const filterKey = generateFilterKeyFromCandidate(candidateFilter);

        // Check if already used in this set or excluded
        return !excludeFilterKeys.has(filterKey);
    };

    // Helper to create filter object
    const createFilter = (type, data) => ({ type, data });

    // Priority 1: Strong Attribute filters
    if (strongAttributeFilters?.length > 0) {
        for (const filter of strongAttributeFilters) {
            const candidateFilter = { type: 'strongAttribute', data: filter };
            if (isFilterUsable(candidateFilter, activeFilterNames)) {
                if (currentIndex === targetIndex) {
                    return createFilter('strongAttribute', filter);
                }
                currentIndex += 1;
            }
        }
    }

    // Priority 2a: Truck filter
    const thirdRowFilterOptions = featureFilterOptions?.options?.find(
        (option) => option.value === '1306'
    );
    const cabSizeApplied = cabSizeValues.length > 0;

    const shouldShowTruckCard = isTruckSelected && !cabSizeApplied && !_isEmpty(cabSizeOptions);

    if (shouldShowTruckCard) {
        const candidateFilter = { type: 'truck', data: {} };
        if (isFilterUsable(candidateFilter, activeFilterNames)) {
            if (currentIndex === targetIndex) {
                return createFilter('truck', {});
            }
            currentIndex += 1;
        }
    }

    // Priority 2b: SUV filter
    const thirdRowApplied = featureFilterValues.includes('1306');
    const shouldShowSUVCard = isSUVSelected && !thirdRowApplied && !_isEmpty(thirdRowFilterOptions);

    if (shouldShowSUVCard) {
        const candidateFilter = { type: 'suv', data: {} };
        if (isFilterUsable(candidateFilter, activeFilterNames)) {
            if (currentIndex === targetIndex) {
                return createFilter('suv', {});
            }
            currentIndex += 1;
        }
    }

    // Priority 3: Vehicle Features
    const availableVehicleFeatures = getAvailableVehicleFeatures(featureFilterOptions);

    for (const feature of availableVehicleFeatures) {
        const candidateFilter = { type: 'vehicleFeature', data: feature };
        if (isFilterUsable(candidateFilter, activeFilterNames, featureFilterValues)) {
            if (currentIndex === targetIndex) {
                return createFilter('vehicleFeature', feature);
            }
            currentIndex += 1;
        }
    }

    // Priority 4: Suggested filters
    if (suggestedFilters?.length > 0) {
        for (const filter of suggestedFilters) {
            const candidateFilter = { type: 'suggested', data: filter };
            if (isFilterUsable(candidateFilter, activeFilterNames)) {
                if (currentIndex === targetIndex) {
                    return createFilter('suggested', filter);
                }
                currentIndex += 1;
            }
        }
    }

    return null; // No filter found at the target index
};

/**
 * Calculate vehicle style state (truck/SUV card logic)
 */
const calculateVehicleStyleState = (params) => {
    const {
        firstStrongAttribute,
        isTruckSelected,
        isSUVSelected,
        featureFilterOptions,
        featureFilterValues,
        cabSizeOptions,
        cabSizeValues,
    } = params;

    const thirdRowFilterOptions = featureFilterOptions?.options?.find(
        (option) => option.value === '1306'
    );
    const thirdRowApplied = featureFilterValues.includes('1306');
    const cabSizeApplied = cabSizeValues.length > 0;

    // Truck card logic
    const shouldShowTruckCard = !firstStrongAttribute
                               && isTruckSelected
                               && !cabSizeApplied
                               && !_isEmpty(cabSizeOptions);

    const isTruckCard = shouldShowTruckCard;

    // SUV card logic
    const shouldShowSUVCard = !firstStrongAttribute
                             && !isTruckCard
                             && isSUVSelected
                             && !thirdRowApplied
                             && !_isEmpty(thirdRowFilterOptions);

    const isSUVCard = shouldShowSUVCard;

    return {
        isTruckCard,
        isSUVCard,
        thirdRowApplied,
        cabSizeApplied,
    };
};

/**
 * Calculate vehicle feature state
 */
const calculateVehicleFeatureState = (params) => {
    const {
        firstStrongAttribute,
        isTruckCard,
        isSUVCard,
        featureFilterOptions,
        featureFilterValues = [],
        activeFilterNames = [],
    } = params;

    const availableVehicleFeatures = getAvailableVehicleFeatures(featureFilterOptions);
    const hasVehicleFeatures = !_isEmpty(availableVehicleFeatures);
    const firstUnseenVehicleFeature = getFirstUnseenVehicleFeature(availableVehicleFeatures, featureFilterValues, activeFilterNames);
    const hasUnseenVehicleFeatures = Boolean(firstUnseenVehicleFeature);

    const shouldShowVehicleFeatureCard = !firstStrongAttribute
                                        && !isTruckCard
                                        && !isSUVCard
                                        && hasVehicleFeatures
                                        && hasUnseenVehicleFeatures;

    const isVehicleFeatureCard = shouldShowVehicleFeatureCard && Boolean(firstUnseenVehicleFeature);

    return {
        isVehicleFeatureCard,
        availableVehicleFeatures,
        hasVehicleFeatures,
        hasUnseenVehicleFeatures,
        firstUnseenVehicleFeature,
    };
};

/**
 * Calculate filter hierarchy based on priority rules
 */
export const getFilterHierarchy = (params = {}) => {
    const {
        activeFilterValues = [],
        suggestedFilters = [],
        strongAttributeFilters = [],
        isTruckSelected = false,
        isSUVSelected = false,
        featureFilterOptions = null,
        featureFilterValues = [],
        cabSizeOptions = [],
        cabSizeValues = [],
    } = params;

    // Helper to get active filter names
    const activeFilterNames = (activeFilterValues || []).map((filter) => filter.filterName);

    // Find first inactive filters
    const firstStrongAttribute = strongAttributeFilters?.find((filter) => !isFilterAlreadyActive(filter, activeFilterNames)) || null;

    const firstSuggestedFilter = suggestedFilters?.find((filter) => !isFilterAlreadyActive(filter, activeFilterNames)) || null;

    // Priority 1: Strong Attributes
    const isStrongAttribute = Boolean(firstStrongAttribute);

    // Priority 2: Truck/SUV Bodystyle substyle
    const vehicleStyleState = calculateVehicleStyleState({
        firstStrongAttribute,
        isTruckSelected,
        isSUVSelected,
        featureFilterOptions,
        featureFilterValues,
        cabSizeOptions,
        cabSizeValues,
    });

    // Priority 3: Vehicle Features
    const vehicleFeatureState = calculateVehicleFeatureState({
        firstStrongAttribute,
        isTruckCard: vehicleStyleState.isTruckCard,
        isSUVCard: vehicleStyleState.isSUVCard,
        featureFilterOptions,
        featureFilterValues,
        activeFilterNames,
    });

    // Priority 4: Suggested Filters
    const isSuggestedFilter = !isStrongAttribute
                             && !vehicleStyleState.isTruckCard
                             && !vehicleStyleState.isSUVCard
                             && !vehicleFeatureState.isVehicleFeatureCard
                             && Boolean(firstSuggestedFilter);

    return {
        // Priority flags
        isStrongAttribute,
        isTruckCard: vehicleStyleState.isTruckCard,
        isSUVCard: vehicleStyleState.isSUVCard,
        isVehicleFeatureCard: vehicleFeatureState.isVehicleFeatureCard,
        isSuggestedFilter,

        // Filter data
        firstStrongAttribute,
        firstSuggestedFilter,
        randomVehicleFeature: vehicleFeatureState.firstUnseenVehicleFeature,
        availableVehicleFeatures: vehicleFeatureState.availableVehicleFeatures,

        // Helper values
        activeFilterNames,
        hasVehicleFeatures: vehicleFeatureState.hasVehicleFeatures,
        hasUnseenVehicleFeatures: vehicleFeatureState.hasUnseenVehicleFeatures,
        thirdRowApplied: vehicleStyleState.thirdRowApplied,
        cabSizeApplied: vehicleStyleState.cabSizeApplied,
    };
};

export default getFilterHierarchy;
